import {
  animate,
  animateChild,
  group,
  query,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
  TemplateRef,
} from '@angular/core';
import { IconDefinition } from '@fortawesome/angular-fontawesome';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { ScreenService } from '@catch/services';

@Component({
  selector: 'ct-lib-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  standalone: true,
  imports: [NgTemplateOutlet, FaIconComponent, TranslocoPipe],
  animations: [
    trigger('backdrop', [
      transition(':enter', [
        group([
          query('@modal', [animateChild()]),
          style({ opacity: '0' }),
          animate(
            '0.3s ease',
            style({
              opacity: '1',
            })
          ),
        ]),
      ]),
      transition(':leave', [
        group([
          query('@modal', [animateChild()]),
          animate(
            '0.3s ease',
            style({
              opacity: '0',
            })
          ),
        ]),
      ]),
    ]),
    trigger('modal', [
      transition(':enter', [
        style({ opacity: '0', transform: 'scale(0.95)' }),
        animate(
          '0.3s ease',
          style({
            opacity: '1',
            transform: 'scale(1)',
          })
        ),
      ]),
      transition(':leave', [
        animate(
          '0.3s ease',
          style({
            opacity: '0',
            transform: 'scale(0.95)',
          })
        ),
      ]),
    ]),
  ],
})
export class ModalComponent {
  @Input() label?: string;
  @Input() icon?: IconDefinition;
  @Input() footer?: TemplateRef<unknown>;
  @Input() closeable = true;
  @Input() small = false;
  @Input() medium = false;
  @Input() big = false;
  @Input() hideHeader = false;
  @Input() hideFooter = false;
  @Input() dynamic = false;
  @Output() onClose = new EventEmitter<void>();
  @Output() onCloseEnd = new EventEmitter<void>();
  @Output() onOpen = new EventEmitter<void>();
  @Output() onOpenEnd = new EventEmitter<void>();
  opened = signal<boolean>(false);
  shaking = signal<boolean>(false);
  faTimes = faTimes;
  readonly screenService = inject(ScreenService);

  open(): void {
    if (this.opened()) {
      return;
    }
    this.opened.set(true);
    this.onOpen.emit();
  }

  close(event?: Event): void {
    event?.stopPropagation();
    if (!this.opened()) {
      return;
    }
    this.opened.set(false);
    this.onClose.emit();
  }

  onBackdropPointerDown(event: MouseEvent): void {
    event.stopPropagation();
    if (this.closeable) {
      this.close();
    } else {
      this.shake();
    }
  }

  shake(): void {
    if (!this.opened() || this.shaking()) {
      return;
    }
    this.shaking.set(true);
    setTimeout(() => {
      this.shaking.set(false);
    }, 300);
  }
}
